import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Camera, ArrowsOutCardinal, MagnifyingGlassPlus, MagnifyingGlassMinus, X, Check } from "@phosphor-icons/react";

const MAX_BYTES = 5 * 1024 * 1024;
const OUTPUT_SIZE = 512;

/**
 * AvatarUploader — circular crop uploader.
 * Props:
 *   currentUrl  — existing avatar URL (absolute or /api/...)
 *   onUploaded(url)  — called with the new /api/files/... URL after successful upload
 *   size  — preview size in px (default 160)
 *   label  — optional caption
 */
export default function AvatarUploader({ currentUrl, onUploaded, size = 160, label = "Profile picture" }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const imgRef = useRef(null);

  const displayUrl = currentUrl?.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${currentUrl}`
    : currentUrl;

  const openPicker = () => fileRef.current?.click();

  const onPick = (e) => {
    setError("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) {
      setError("Use a JPG, PNG, or WEBP image.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("Max file size is 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImgSrc(reader.result);
      setScale(1);
      setOffset({ x: 0, y: 0 });
      setModalOpen(true);
    };
    reader.readAsDataURL(f);
  };

  const onDragStart = (e) => {
    if (e.touches) e = e.touches[0];
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onDrag = (e) => {
    if (!dragging) return;
    const t = e.touches ? e.touches[0] : e;
    setOffset({
      x: dragStart.current.ox + (t.clientX - dragStart.current.x),
      y: dragStart.current.oy + (t.clientY - dragStart.current.y),
    });
  };
  const onDragEnd = () => setDragging(false);

  useEffect(() => {
    if (!modalOpen) return;
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", onDragEnd);
    return () => {
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("mouseup", onDragEnd);
    };
    // eslint-disable-next-line
  }, [modalOpen, dragging, offset.x, offset.y]);

  const cancel = () => {
    setModalOpen(false);
    setImgSrc(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const renderAndUpload = async () => {
    if (!imgRef.current) return;
    setBusy(true);
    setError("");
    try {
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0A0A0C";
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      // crop circle
      ctx.save();
      ctx.beginPath();
      ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      const img = imgRef.current;
      const previewSize = 320; // modal preview size in px
      // ratio of output to preview so transforms map
      const r = OUTPUT_SIZE / previewSize;
      // image natural size scaled to fit preview at scale=1 (contain)
      const baseFit = Math.min(previewSize / img.naturalWidth, previewSize / img.naturalHeight);
      const drawW = img.naturalWidth * baseFit * scale * r;
      const drawH = img.naturalHeight * baseFit * scale * r;
      const cx = OUTPUT_SIZE / 2 + offset.x * r;
      const cy = OUTPUT_SIZE / 2 + offset.y * r;
      ctx.drawImage(img, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
      ctx.restore();

      const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.9));
      const fd = new FormData();
      fd.append("file", new File([blob], `avatar-${Date.now()}.png`, { type: "image/png" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded?.(data.url);
      cancel();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="inline-block" data-testid="avatar-uploader">
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} className="hidden" data-testid="avatar-file-input" />

      <button type="button" onClick={openPicker} className="group relative block" style={{ width: size, height: size }} data-testid="avatar-uploader-trigger">
        <div className="w-full h-full rounded-full overflow-hidden border-2 border-[#27272A] bg-[#1A1A1D]">
          {displayUrl ? (
            <img src={displayUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center label-mono text-zinc-500">
              <Camera size={size / 3} weight="light" />
            </div>
          )}
        </div>
        <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity">
          <Camera size={size / 4} className="text-white" />
        </div>
      </button>
      <div className="label-mono mt-2 text-center" style={{ fontSize: 9 }}>{label}</div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="avatar-crop-modal" onClick={cancel}>
          <div className="card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="label-mono" style={{ color: "#E60012" }}>CROP TO CIRCLE</div>
              <button onClick={cancel} className="btn btn-ghost btn-sm"><X size={14} /></button>
            </div>

            <div
              className="relative mx-auto mb-4 overflow-hidden bg-[#0A0A0C] border border-[#27272A] select-none"
              style={{ width: 320, height: 320, borderRadius: 9999, cursor: dragging ? "grabbing" : "grab" }}
              onMouseDown={onDragStart}
              onTouchStart={onDragStart}
              onTouchMove={onDrag}
              onTouchEnd={onDragEnd}
            >
              {imgSrc && (
                <img
                  ref={imgRef}
                  src={imgSrc}
                  alt=""
                  className="absolute pointer-events-none select-none"
                  style={{
                    left: "50%",
                    top: "50%",
                    transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale})`,
                    maxWidth: 320,
                    maxHeight: 320,
                  }}
                  draggable={false}
                />
              )}
              {/* outline ring */}
              <div className="absolute inset-0 rounded-full border-2 border-[#E60012] pointer-events-none" />
            </div>

            {/* zoom slider */}
            <div className="flex items-center gap-3 mb-3">
              <MagnifyingGlassMinus size={14} className="text-zinc-400" />
              <input
                type="range"
                min={0.5}
                max={3}
                step={0.01}
                value={scale}
                onChange={(e) => setScale(parseFloat(e.target.value))}
                className="flex-1"
                data-testid="avatar-zoom"
              />
              <MagnifyingGlassPlus size={14} className="text-zinc-400" />
            </div>
            <div className="text-center label-mono mb-4" style={{ fontSize: 9 }}>
              <ArrowsOutCardinal size={11} className="inline mr-1" /> Drag to reposition · scroll/range to zoom
            </div>

            {error && <div className="text-xs text-red-500 mb-3 text-center" data-testid="avatar-error">{error}</div>}

            <div className="flex justify-end gap-2">
              <button onClick={cancel} className="btn btn-ghost btn-sm" data-testid="avatar-cancel">Cancel</button>
              <button onClick={renderAndUpload} disabled={busy} className="btn btn-primary btn-sm" data-testid="avatar-save">
                {busy ? <span className="spinner" /> : <><Check size={12} weight="bold" /> Save</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
